import Button from '@carvana/ds-button';
import { CheckCircleSpot } from '@carvana/ds-icons';
import List from '@carvana/ds-list';
import { icon } from '@carvana/ds-styles';
import cx from 'classnames';
import { Check } from 'lucide-react';
import React from 'react';
import EventedModal from '|/app/modals/evented-modal';
import { ModalNames } from '|/app/modals/modal-events';
import { ExampleVisualization } from '|/app/purchase/cart/steps/gap-coverage';
import { StepName } from '|/app/services';
import { buildFinancingImpactStripText } from '|/helpers/carvana-care-modal-variants/carvana-care-modal-variants';
import currency from '|/helpers/currency';
import useNavigateToStep from '|/hooks/use-navigate-to-step';
import styles from './gap-coverage-modal.module.css';

export interface GapCoverageModalProps {
  isOpen: boolean;
  onClose: () => void;
  impactsFinancingTerms: boolean;
  vehicleModel?: string;
  termMonths?: number;
  monthlyDelta?: number;
  downDelta?: number;
  termDelta?: number;
  /** See `deltasBetweenLoans` — false means the deltas are placeholders, not real zeros. */
  hasLoanComparison?: boolean;
  fullPrice?: number;
  isFinanced?: boolean;
  /**
   * pp-check-it-out only: re-prices the cost row. Control
   * (`price-breakdown.tsx`) keeps the original card, so this is one flag rather than a fork.
   */
  useSnapshotPricing?: boolean;
}

const formatWholeDollars = (value: number) =>
  currency(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const GapCoverageModal = ({
  isOpen,
  onClose,
  impactsFinancingTerms,
  vehicleModel = 'vehicle',
  termMonths = 0,
  monthlyDelta = 0,
  downDelta = 0,
  termDelta = 0,
  hasLoanComparison = false,
  fullPrice = 0,
  isFinanced = true,
  useSnapshotPricing = false
}: GapCoverageModalProps) => {
  const navigate = useNavigateToStep();

  /**
   * Drops the monthly framing once GAP moves the down payment, matching CarvanaCare — see the
   * equivalent block there for why `hasLoanComparison` is load-bearing.
   */
  const showAsMonthly = useSnapshotPricing
    ? isFinanced && hasLoanComparison && downDelta === 0
    : isFinanced;

  /**
   * "Coverage for N months / Or until your loan is paid off" is the Carvana loan's term, so it
   * is a claim only a Carvana-financed customer's data supports. Cash and SAF customers have no
   * `funding.loan`, which makes this render "Coverage for 0 months" next to a reference to a
   * loan they don't have. No GAP-contract term exists on the summary payload to substitute, so
   * the price shows alone.
   */
  const showCoverageTerm = useSnapshotPricing ? termMonths > 0 : true;

  const modalEventMetadata: Record<string, unknown> = {
    product: 'GAP',
    ...(showAsMonthly
      ? { monthlyPrice: monthlyDelta, downPayment: downDelta, termDelta, termMonths }
      : { fullPrice })
  };

  return (
    <EventedModal
      modalHeader=""
      aria-label="GAP coverage details"
      mobileStyle="inset"
      desktopWidth="480px"
      divLine={false}
      contentPadding={false}
      isOpen={isOpen}
      onClose={onClose}
      analyticsId={ModalNames.GapCoverage}
      eventMetadata={modalEventMetadata}
    >
      <div
        className={cx(styles.container, useSnapshotPricing && styles.snapshotContainer)}
        data-testid="gap-coverage-modal-container"
      >
        <div className={cx(styles.content, useSnapshotPricing && styles.snapshotContent)}>
          <div className={cx(styles.header, useSnapshotPricing && styles.snapshotHeader)}>
            <CheckCircleSpot
              size={32}
              color={icon.primary}
              className={styles.headerIcon}
              data-testid="gap-coverage-header-icon"
            />
            <h2
              className={cx(styles.headerTitle, useSnapshotPricing && styles.snapshotHeaderTitle)}
              data-testid="gap-coverage-heading"
            >
              Your {vehicleModel} is protected with GAP coverage
            </h2>
          </div>

          <div
            className={cx(styles.costCard, useSnapshotPricing && styles.snapshotCostCard)}
            data-testid="gap-coverage-cost-card"
          >
            <div className={styles.costRow}>
              {/* Kept even when empty: `.costRow` is `space-between`, so this placeholder is
                  what keeps the lone price right-aligned. */}
              <div className={styles.costLabels}>
                {showCoverageTerm && (
                  <>
                    <div className={styles.costLabelPrimary}>Coverage for {termMonths} months</div>
                    <div className={styles.costLabelSecondary}>Or until your loan is paid off</div>
                  </>
                )}
              </div>
              <div className={styles.costAmount}>
                {showAsMonthly ? (
                  <>
                    <span className={styles.costAmountStrong} data-testid="gap-coverage-monthly">
                      {/* Figma omits the "+" here but keeps it on CarvanaCare Essential. */}
                      {!useSnapshotPricing && '+'}
                      {formatWholeDollars(monthlyDelta)}
                    </span>
                    <span className={styles.costAmountSuffix}>/mo</span>
                  </>
                ) : (
                  <span className={styles.costAmountStrong} data-testid="gap-coverage-full-price">
                    {formatWholeDollars(fullPrice)}
                  </span>
                )}
              </div>
            </div>
            {!useSnapshotPricing && showAsMonthly && impactsFinancingTerms && (
              <div className={styles.termsChanged} data-testid="gap-coverage-terms-changed">
                {buildFinancingImpactStripText({ downDelta, termDelta })}
              </div>
            )}
          </div>

          <div
            className={cx(styles.bulletSection, useSnapshotPricing && styles.snapshotBulletSection)}
          >
            <List
              variant="icon"
              size="small"
              className={cx(styles.bulletList, useSnapshotPricing && styles.snapshotBulletList)}
              testId="gap-coverage-bullets"
              icon={
                <Check
                  size={16}
                  color={icon.primary}
                  className={styles.bulletIcon}
                  data-testid="check-icon"
                />
              }
              items={[
                {
                  key: 'total-loss',
                  content: (
                    <span className={styles.bulletText}>
                      Helps reduce out-of pocket costs if your car is declared a total loss due to
                      an accident or theft*
                    </span>
                  )
                },
                {
                  key: 'loan-balance',
                  content: (
                    <span className={styles.bulletText}>
                      Can cover the difference between insurance payout and loan balance*
                    </span>
                  )
                },
                {
                  key: 'cancel',
                  content: (
                    <span className={styles.bulletText}>Cancel anytime for a partial refund</span>
                  )
                }
              ]}
            />

            <p className={styles.disclaimer} data-testid="gap-coverage-disclaimer">
              *Refer to your Gap Addendum for refund details and limitations, and for total loss
              details.
            </p>
          </div>

          <div data-testid="gap-coverage-example">
            <ExampleVisualization />
          </div>
        </div>

        <div className={styles.actions}>
          <Button
            variant="tertiary"
            size="md"
            width="100%"
            data-testid="edit-gap-coverage-button"
            data-analytics-id="pp_gap_coverage_modal_edit"
            onClick={() => {
              navigate(StepName.Gap);
            }}
          >
            Edit
          </Button>
          <Button
            variant="primary"
            size="md"
            width="100%"
            data-testid="close-gap-coverage-button"
            data-analytics-id="pp_gap_coverage_modal_close"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </EventedModal>
  );
};

export default GapCoverageModal;
